import { useState } from 'react';
import { Plus, X, Search, RotateCcw, Loader2, CheckCircle, Info } from 'lucide-react';
import PageAiGuide from './PageAiGuide';
import FieldGuide from './FieldGuide';

const MODEL_HINTS = [
  { kind: 'explain', field: '模型类型', text: '四类：纯文本/多模态/向量/重排。超级智能体需选「纯文本 + 支持 Function Calling」的模型；知识库检索需「向量 + 重排」模型。' },
  { kind: 'fact', field: '模型api地址 / 授权密钥', text: '事实型字段，需按现场实际填写，AI 无法代填；填后用「连接测试」验证。' },
  { kind: 'recommend', field: 'Temperature', text: '纯文本/多模态模型的随机性，越低越稳定。', value: '0.8' },
  { kind: 'recommend', field: '向量维度', text: '向量模型维度，行业常用默认值。', value: '1536' },
  { kind: 'recommend', field: '重排 TopK / 相似度阈值', text: '重排模型返回切片数与过滤阈值。', value: 'TopK 5 / 阈值 0.6' },
  { kind: 'explain', field: '调度优先级 / 熔断', text: '同类型按调度优先级升序选用（数字越小越优先）；故障自动熔断隔离并切换备份，心跳探针恢复后回切。' },
];

// 模型类型 → 色 tag
const TYPE_TAG = {
  text: { label: '纯文本模型', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
  multimodal: { label: '多模态模型', cls: 'bg-green-50 text-green-600 border-green-200' },
  vector: { label: '向量模型', cls: 'bg-purple-50 text-purple-600 border-purple-200' },
  rerank: { label: '重排模型', cls: 'bg-orange-50 text-orange-600 border-orange-200' },
};
const STATUS = {
  running: { label: '运行中', dot: 'bg-green-500', text: 'text-green-600' },
  disabled: { label: '禁用', dot: 'bg-gray-400', text: 'text-gray-400' },
  fused: { label: '熔断隔离', dot: 'bg-red-500', text: 'text-red-500' },
};

const MODELS = [
  { id: 1, name: 'JIUTIAN3.0-72B-INSTRUCT', series: '九天', type: 'text', status: 'running', prio: 1, sched: '30s | 3次 | 间隔5s', time: '2025-11-1 10:00:00' },
  { id: 2, name: 'JIUTIAN3.0-72B-INSTRUCT', series: '九天', type: 'text', status: 'running', prio: 2, sched: '30s | 3次 | 间隔5s', time: '2025-11-1 10:00:00' },
  { id: 3, name: 'DeepSeek-R1', series: 'DeepSeek', type: 'vector', status: 'disabled', prio: 3, sched: '30s | 3次 | 间隔5s', time: '2025-11-1 10:00:00' },
  { id: 4, name: 'DeepSeek-R1', series: 'DeepSeek', type: 'rerank', status: 'running', prio: 3, sched: '30s | 3次 | 间隔5s', time: '2025-11-1 10:00:00' },
  { id: 5, name: 'DeepSeek-R1', series: 'DeepSeek', type: 'multimodal', status: 'fused', prio: 2, sched: '30s | 3次 | 间隔5s', time: '2025-11-1 10:00:00', err: 'Connect Timeout' },
];

// ── 模型注册（2 步向导）──────────────────────────────────────────────────
function RegisterModal({ open, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ series: '', name: '', type: 'text', apiUrl: '' });
  const [keys, setKeys] = useState([{ secret: '', usage: '', enabled: true }]);
  const [attr, setAttr] = useState({ temp: '0.5', ctx: '3', maxtok: '8092', dim: '768', maxlen: '8192', topk: '3', thr: '0.6' });
  const setAttr2 = (k, v) => setAttr(p => ({ ...p, [k]: v }));
  const [testState, setTestState] = useState('idle');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  if (!open) return null;

  const setKey = (i, k, v) => setKeys(prev => prev.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const runTest = () => { setTestState('testing'); setTimeout(() => setTestState('ok'), 1000); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[680px] max-h-[88vh] bg-white rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-200">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">‹</button>
          <h3 className="text-base font-semibold text-gray-900">模型注册</h3>
        </div>

        {/* 步骤条 */}
        <div className="flex items-center px-8 py-4 bg-gray-50">
          {['基础配置', '属性配置'].map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-2 px-4 py-1.5 rounded ${step === i + 1 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center ${step >= i + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>{i + 1}</span>{s}
              </div>
              {i === 0 && <div className={`flex-1 h-0.5 ${step > 1 ? 'bg-blue-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {step === 1 ? (
            <>
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-1 h-4 bg-blue-500 rounded" /><span className="text-sm font-semibold text-gray-800">基本信息</span></div>
                <div className="space-y-3">
                  <Field label="模型系列" required>
                    <FieldGuide label="模型系列" value={form.series} meta={{ nature: 'info', explain: '模型供应商/系列（通义千问、九天、DeepSeek、火山引擎…），决定调用协议适配；请按实际接入的模型来源选择。' }}>
                      <select value={form.series} onChange={e => set('series', e.target.value)} className="inp"><option value="">请选择</option><option>通义千问</option><option>九天</option><option>DeepSeek</option><option>火山引擎</option></select>
                    </FieldGuide>
                  </Field>
                  <Field label="模型名称" required>
                    <FieldGuide label="模型名称" value={form.name} meta={{ nature: 'info', explain: '本实例内模型的唯一标识，由英文、数字、短横线组成（如 Qwen2.5-14B-instruct），最多 64 字符；建议与上游真实模型名一致便于辨认。' }}>
                      <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="由英文、数字、短横线组成，如 Qwen2.5-14B-instruct，最多64个字符" className="inp" />
                    </FieldGuide>
                  </Field>
                  <Field label="模型类型" required>
                    <FieldGuide label="模型类型" value={form.type} meta={{ nature: 'info', explain: '四类：纯文本/多模态/向量/重排。超级智能体需「纯文本 + 支持 Function Calling」；知识库检索需「向量 + 重排」。选定后下一步的属性配置项随之联动。' }}>
                      <select value={form.type} onChange={e => set('type', e.target.value)} className="inp"><option value="text">纯文本模型</option><option value="multimodal">多模态通用大模型</option><option value="vector">向量模型</option><option value="rerank">重排模型</option></select>
                    </FieldGuide>
                  </Field>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3"><div className="w-1 h-4 bg-blue-500 rounded" /><span className="text-sm font-semibold text-gray-800">连接配置</span>
                  <button onClick={runTest} className="text-xs text-blue-600 hover:text-blue-700 ml-1">连接测试</button>
                  <span className="text-[11px] text-gray-400">（可选，建议）</span>
                  {testState === 'testing' && <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
                  {testState === 'ok' && <span className="text-xs text-green-600 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /> 连接正常</span>}
                </div>
                <Field label="模型api地址" required>
                  <FieldGuide label="模型api地址" value={form.apiUrl} meta={{ nature: 'fact', explain: '模型服务的访问端点，事实型字段，需按现场实际部署填写（如 <IP>:<PORT>/qwen/v1），AI 无法代填；填后用「连接测试」验证。' }}>
                    <input value={form.apiUrl} onChange={e => set('apiUrl', e.target.value)} placeholder="满足标准范式，如：<IP>:<PORT>/qwen/v1" className="inp font-mono" />
                  </FieldGuide>
                </Field>
                {keys.map((k, i) => (
                  <div key={i} className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                    <Field label="授权密钥" required>
                      <FieldGuide label="授权密钥" value={k.secret} meta={{ nature: 'fact', explain: '调用上游模型服务的 API Key / Token，事实型敏感字段，需按现场实际填写，AI 无法代填；平台加密存储、调用时自动注入。' }}>
                        <input type="password" value={k.secret} onChange={e => setKey(i, 'secret', e.target.value)} placeholder="授权密钥" className="inp" />
                      </FieldGuide>
                    </Field>
                    <Field label="用途" required>
                      <FieldGuide label="用途" value={k.usage} meta={{ nature: 'info', explain: '该密钥的备注用途（如「生产-主」「测试」），便于多密钥时辨识管理；不影响调用。' }}>
                        <input value={k.usage} onChange={e => setKey(i, 'usage', e.target.value)} placeholder="密钥用途" className="inp" />
                      </FieldGuide>
                    </Field>
                    <div className="flex items-center gap-2 text-sm text-gray-600"><span>启用此密钥：</span><input type="checkbox" checked={k.enabled} onChange={e => setKey(i, 'enabled', e.target.checked)} className="accent-blue-600" /></div>
                  </div>
                ))}
                <button onClick={() => setKeys(prev => [...prev, { secret: '', usage: '', enabled: false }])} className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus className="w-3 h-3" /> 添加密钥</button>
              </div>
            </>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-3"><div className="w-1 h-4 bg-blue-500 rounded" /><span className="text-sm font-semibold text-gray-800">属性配置</span></div>
              <p className="text-xs text-gray-400 mb-3 flex items-center gap-1"><Info className="w-3 h-3" /> 属性配置项随「模型类型」联动；<span className="text-indigo-500">点击/聚焦输入框 → 右下方弹出该字段的 AI 实时引导（解释 + 经验型可采纳推荐值）</span></p>
              {(form.type === 'text' || form.type === 'multimodal') && (
                <div className="space-y-3">
                  <Field label="Temperature">
                    <FieldGuide label="Temperature" value={attr.temp} meta={{ nature: 'experience', explain: '采样随机性，0~1，越低越稳定、越高越发散。配置类任务建议偏低。', recommend: '0.8' }} onAdopt={(v) => setAttr2('temp', v)}>
                      <input value={attr.temp} onChange={e => setAttr2('temp', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                  <Field label="携带上下文轮数">
                    <FieldGuide label="携带上下文轮数" value={attr.ctx} meta={{ nature: 'experience', explain: '每次请求携带的历史对话轮数，1~10。越多越懂上下文但越费 token。', recommend: '3' }} onAdopt={(v) => setAttr2('ctx', v)}>
                      <input value={attr.ctx} onChange={e => setAttr2('ctx', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                  <Field label="max_tokens">
                    <FieldGuide label="max_tokens" value={attr.maxtok} meta={{ nature: 'experience', explain: '单次生成的最大 token 数上限。', recommend: '8092' }} onAdopt={(v) => setAttr2('maxtok', v)}>
                      <input value={attr.maxtok} onChange={e => setAttr2('maxtok', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                  {form.type === 'multimodal' && <Field label="支持媒体类型"><div className="flex gap-3 text-sm text-gray-600">{['图片', '视频', '音频'].map(m => <label key={m} className="flex items-center gap-1"><input type="checkbox" className="accent-blue-600" />{m}</label>)}</div></Field>}
                </div>
              )}
              {form.type === 'vector' && (
                <div className="space-y-3">
                  <Field label="向量维度">
                    <FieldGuide label="向量维度" value={attr.dim} meta={{ nature: 'experience', explain: '向量化输出维度，需与所选向量模型一致。1536 是 OpenAI 设定、Dify/Coze 常用的行业标杆默认值。', recommend: '1536' }} onAdopt={(v) => setAttr2('dim', v)}>
                      <input value={attr.dim} onChange={e => setAttr2('dim', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                  <Field label="模型最大支持长度">
                    <FieldGuide label="模型最大支持长度" value={attr.maxlen} meta={{ nature: 'experience', explain: '作为全局约束：切片 Max Chunk Size 不能超过此物理限值。', recommend: '8192' }} onAdopt={(v) => setAttr2('maxlen', v)}>
                      <input value={attr.maxlen} onChange={e => setAttr2('maxlen', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                </div>
              )}
              {form.type === 'rerank' && (
                <div className="space-y-3">
                  <Field label="TopK 默认值（1~20）">
                    <FieldGuide label="TopK" value={attr.topk} meta={{ nature: 'experience', explain: '重排后默认返回的切片数，1~20。', recommend: '5' }} onAdopt={(v) => setAttr2('topk', v)}>
                      <input value={attr.topk} onChange={e => setAttr2('topk', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                  <Field label="相似度阈值（0~1，步进0.05）">
                    <FieldGuide label="相似度阈值" value={attr.thr} meta={{ nature: 'experience', explain: '仅返回相似度高于此值的结果，越接近 1 越严格。', recommend: '0.6' }} onAdopt={(v) => setAttr2('thr', v)}>
                      <input value={attr.thr} onChange={e => setAttr2('thr', e.target.value)} className="inp" />
                    </FieldGuide>
                  </Field>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-center gap-3">
          <button onClick={step === 1 ? onClose : () => setStep(1)} className="px-5 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">{step === 1 ? '取消' : '上一步'}</button>
          {step === 1
            ? (() => { const ok = form.series && form.name && form.apiUrl; return <button onClick={() => ok && setStep(2)} disabled={!ok} title={ok ? '' : '请先填写：模型系列、模型名称、模型api地址'} className={`px-5 py-2 text-sm rounded-lg ${ok ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>下一步</button>; })()
            : <button onClick={() => { onDone && onDone(form.name); onClose(); }} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">完成注册</button>}
        </div>
      </div>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <div className="grid grid-cols-[100px_1fr] items-center gap-3">
      <label className="text-sm text-gray-600 text-right">{required && <span className="text-red-500">* </span>}{label}：</label>
      {children}
    </div>
  );
}

export default function ModelManagement() {
  const [models, setModels] = useState(MODELS);
  const [regOpen, setRegOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [fType, setFType] = useState('');

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2200); };
  const toggle = (id) => setModels(prev => prev.map(m => m.id === id ? { ...m, status: m.status === 'running' ? 'disabled' : 'running' } : m));
  const filtered = models.filter(m => !fType || m.type === fType);

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <style>{`.inp{width:100%;padding:8px 12px;font-size:14px;border:1px solid #e5e7eb;border-radius:8px;outline:none}.inp:focus{box-shadow:0 0 0 2px #3b82f6}`}</style>
      <RegisterModal open={regOpen} onClose={() => setRegOpen(false)} onDone={(n) => showToast(`模型「${n}」已注册`)} />
      {toast && <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />{toast}</div>}

      <h2 className="text-xl font-bold text-gray-900 mb-1">大模型管理</h2>
      <p className="text-sm text-gray-500 mb-5">四类模型统一治理（纯文本/多模态/向量/重排）+ 主从切换 + 熔断自愈。供本实例超级智能体与知识库调用。</p>

      {/* 筛选栏 */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2"><span className="text-sm text-gray-600">模型名称：</span><input placeholder="请输入" className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg w-40" /></div>
          <div className="flex items-center gap-2"><span className="text-sm text-gray-600">模型系列：</span><select className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg"><option>通义千问</option><option>九天</option><option>DeepSeek</option></select></div>
          <div className="flex items-center gap-2"><span className="text-sm text-gray-600">模型类型：</span><select value={fType} onChange={e => setFType(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg"><option value="">全部</option><option value="text">纯文本模型</option><option value="multimodal">多模态模型</option><option value="vector">向量模型</option><option value="rerank">重排模型</option></select></div>
          <button className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">查询</button>
          <button className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> 重置</button>
          <button onClick={() => setRegOpen(true)} className="ml-auto px-4 py-1.5 bg-blue-50 text-blue-600 text-sm rounded-lg hover:bg-blue-100 flex items-center gap-1"><Plus className="w-4 h-4" /> 模型注册</button>
        </div>
      </div>

      {/* 列表 */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-xs text-gray-500">
            <th className="text-left px-4 py-3 font-medium w-12">序号</th>
            <th className="text-left px-4 py-3 font-medium">模型名称</th>
            <th className="text-left px-4 py-3 font-medium">模型类型</th>
            <th className="text-left px-4 py-3 font-medium">状态</th>
            <th className="text-left px-4 py-3 font-medium">调度优先级 ↕</th>
            <th className="text-left px-4 py-3 font-medium">调度策略</th>
            <th className="text-left px-4 py-3 font-medium">更新时间</th>
            <th className="text-left px-4 py-3 font-medium">操作</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((m, i) => {
              const tt = TYPE_TAG[m.type]; const st = STATUS[m.status];
              return (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-700">{m.name}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded border ${tt.cls}`}>{tt.label}</span></td>
                  <td className="px-4 py-3"><span className={`flex items-center gap-1.5 text-xs ${st.text}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}{m.status === 'fused' && <span title={m.err} className="text-red-400 cursor-help">ⓘ</span>}</span></td>
                  <td className="px-4 py-3 text-gray-600">{m.prio}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{m.sched}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{m.time}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      <button onClick={() => toggle(m.id)} className="text-blue-600 hover:text-blue-700">{m.status === 'running' ? '禁用' : '启用'}</button>
                      <button className="text-blue-600 hover:text-blue-700">编辑</button>
                      <button onClick={() => showToast(`「${m.name}」连接测试通过`)} className="text-blue-600 hover:text-blue-700">连接测试</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          共 25 条　<select className="border border-gray-200 rounded px-1 py-0.5"><option>5条/页</option></select>　 1 2 3 4 5 　前往 <input className="w-10 border border-gray-200 rounded px-1 py-0.5" defaultValue="1" /> 页
        </div>
      </div>
      <PageAiGuide pageName="模型管理" hints={MODEL_HINTS} />
    </div>
  );
}
